import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user data exists in localStorage
    // Note: Token is now in httpOnly cookie, not localStorage
    const savedUser = localStorage.getItem('user');
    
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (mobile, password) => {
    const response = await authAPI.login({ mobile, password });
    const { user, access_token } = response.data;
    
    // Store both user data and token (fallback for CORS issues with httpOnly cookies)
    localStorage.setItem('user', JSON.stringify(user));
    if (access_token) {
      localStorage.setItem('token', access_token);
    }
    setUser(user);
    
    return user;
  }, []);

  const register = useCallback(async (userData) => {
    const response = await authAPI.register(userData);
    const { user, access_token } = response.data;
    
    // Store both user data and token (fallback for CORS issues with httpOnly cookies)
    localStorage.setItem('user', JSON.stringify(user));
    if (access_token) {
      localStorage.setItem('token', access_token);
    }
    setUser(user);
    
    return user;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend to clear cookie
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
    }
  }, []);

  const updateUserData = useCallback((updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, []);

  // Optimize context value with useMemo
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateUserData
  }), [user, loading, login, register, logout, updateUserData]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
